import Highcharts from 'highcharts';
import type { ChartConfig, ChartHighlightPoint, ChartPoint, CompetitorChartSeries, MarketShareInsights, ShareType } from '../../types';
import {
  AXIS_VALUE_EPSILON,
  BULLET_Z_INDEX,
  CHART_HEIGHT_PX,
  CHART_SPACING_TOP_PX,
  COLORS,
  DISABLED_HOVER_STATE,
  FALLBACK_MARKER_COLOR,
  FONT_FAMILY,
  HIGHLIGHT_Z_INDEX,
  HOVER_INTERACTION_RADIUS_PX,
  HOVER_TARGET_SERIES_NAME,
  LEGEND_ITEM_GAP_PX,
  SERIES_LINE_WIDTH_PX,
  SPLINE_MARKER_RADIUS_PX,
  SPLINE_VALUE_Z_INDEX,
  SPLINE_VOLUME_Z_INDEX,
  X_AXIS_BREAK_END_OFFSET,
  X_AXIS_BREAK_SIZE,
  X_AXIS_BREAK_START_OFFSET,
  X_AXIS_MIN_MAX_PADDING_RATIO,
  applyPointHoverBackdrop,
  applyHoverBackdropAt,
  buildCircleMarker,
  buildCompetitorLegendIcon,
  clearPointHoverBackdrop,
  isSameXAxisValue,
  svgBlue,
  svgCompetitividade,
  // svgOptimal,
  svgValue,
  organizeLegendWithBottomRow,
} from './chartHelpers';
import { setIndicatorHidden, syncIndicatorBands } from './indicatorRenderer';
import { buildTooltipContent, buildTooltipContentCompetitor } from './tooltipBuilder';
import type { ChartIndicatorsContext, ChartOptionsContext, ChartSeriesContext, IndicatorBandConfig, IndicatorScatterSeriesConfig } from './types';
import { INDICATOR_CPI_BAND_ID, INDICATOR_CPI_LINE_ID, INDICATOR_OPTIMAL_BAND_ID, INDICATOR_OPTIMAL_LINE_ID } from './types';

const getHiddenTickValues = (indicatorsContext: ChartIndicatorsContext) => {
  return [indicatorsContext.cpiIndicator?.x, indicatorsContext.optimalIndicator?.x].filter((value): value is number => typeof value === 'number');
};

const getXAxisTickPositions = (chartData: ChartConfig, indicatorsContext: ChartIndicatorsContext) => {
  const hiddenTickValues = getHiddenTickValues(indicatorsContext);
  const { min, max } = chartData.xAxis;

  return Array.from(new Set([min, ...chartData.xAxis.tickPositions, max]))
    .filter(tickValue => {
      const atAxisBoundary = isSameXAxisValue(tickValue, min) || isSameXAxisValue(tickValue, max);
      if (atAxisBoundary) return true;
      return !hiddenTickValues.some(hiddenValue => isSameXAxisValue(tickValue, hiddenValue));
    })
    .sort((firstTick, secondTick) => firstTick - secondTick);
};

const MIN_X_AXIS_LABEL_PIXEL_GAP = 26;
const MIN_X_AXIS_LABEL_PERCENT_GAP = 2;

const toXAxisLabelPercent = (value: number) => Math.round(value * 100);

/** Remove ticks que renderizariam o mesmo rótulo % (arredondado), evitando hastes “fantasma”. */
const dedupeXAxisTickPositionsByDisplayedPercent = (ticks: number[]) => {
  if (ticks.length <= 2) return ticks;

  const [firstTick, ...rest] = ticks;
  const lastTick = rest[rest.length - 1];
  const middleTicks = rest.slice(0, -1);
  const filteredTicks: number[] = [firstTick];
  let lastKeptPercent = toXAxisLabelPercent(firstTick);

  middleTicks.forEach(tickValue => {
    const tickPercent = toXAxisLabelPercent(tickValue);
    if (tickPercent === lastKeptPercent) return;
    filteredTicks.push(tickValue);
    lastKeptPercent = tickPercent;
  });

  const lastPercent = toXAxisLabelPercent(lastTick);
  if (filteredTicks.length > 0 && lastPercent === toXAxisLabelPercent(filteredTicks[filteredTicks.length - 1])) {
    filteredTicks.pop();
  }

  filteredTicks.push(lastTick);
  return filteredTicks;
};

const filterXAxisTickPositionsByPixelGap = (axis: Highcharts.Axis, ticks: number[], axisMin: number, axisMax: number) => {
  if (ticks.length <= 2) return ticks;

  const [firstTick, ...rest] = ticks;
  const lastTick = rest[rest.length - 1];
  const middleTicks = rest.slice(0, -1);
  const filteredTicks: number[] = [firstTick];

  middleTicks.forEach(tickValue => {
    const lastKept = filteredTicks[filteredTicks.length - 1];
    const tickPx = axis.toPixels(tickValue, true);
    const lastKeptPx = axis.toPixels(lastKept, true);
    const tickPercent = toXAxisLabelPercent(tickValue);
    const lastKeptPercent = toXAxisLabelPercent(lastKept);

    const percentDelta = Math.abs(tickPercent - lastKeptPercent);
    const hasEnoughPercentGap = percentDelta >= MIN_X_AXIS_LABEL_PERCENT_GAP;
    const hasEnoughPixelGap = Number.isFinite(tickPx) && Number.isFinite(lastKeptPx) && Math.abs(tickPx - lastKeptPx) >= MIN_X_AXIS_LABEL_PIXEL_GAP;
    const isAdjacentPercentLabel = percentDelta === 1;
    if (isAdjacentPercentLabel) {
      const distToMin = Math.abs(tickValue - axisMin);
      const distLastKeptToMin = Math.abs(lastKept - axisMin);
      const distToMax = Math.abs(tickValue - axisMax);
      const distLastKeptToMax = Math.abs(lastKept - axisMax);
      const preferTickNearBoundary = distToMin < distLastKeptToMin - AXIS_VALUE_EPSILON || distToMax < distLastKeptToMax - AXIS_VALUE_EPSILON;
      if (!preferTickNearBoundary) return;
    }

    if (hasEnoughPercentGap && hasEnoughPixelGap) {
      filteredTicks.push(tickValue);
    }
  });

  const lastKeptBeforeEnd = filteredTicks[filteredTicks.length - 1];
  const lastKeptPx = axis.toPixels(lastKeptBeforeEnd, true);
  const endPx = axis.toPixels(lastTick, true);
  const lastKeptPercent = toXAxisLabelPercent(lastKeptBeforeEnd);
  const endPercent = toXAxisLabelPercent(lastTick);

  if (
    filteredTicks.length > 1 &&
    ((Number.isFinite(lastKeptPx) && Number.isFinite(endPx) && Math.abs(endPx - lastKeptPx) < MIN_X_AXIS_LABEL_PIXEL_GAP) ||
      Math.abs(endPercent - lastKeptPercent) < MIN_X_AXIS_LABEL_PERCENT_GAP)
  ) {
    filteredTicks.pop();
  }

  filteredTicks.push(lastTick);
  return filteredTicks;
};

const getXAxisBreaks = (chartData: ChartConfig) => {
  const firstDataX = Math.min(...chartData.series.flatMap(series => series.data.map(point => point.x)));
  const xAxisBreakTo = Math.max(chartData.xAxis.min, firstDataX - X_AXIS_BREAK_END_OFFSET);

  if (xAxisBreakTo <= chartData.xAxis.min) {
    return [];
  }

  return [
    {
      from: chartData.xAxis.min + X_AXIS_BREAK_START_OFFSET,
      to: xAxisBreakTo,
      breakSize: X_AXIS_BREAK_SIZE,
    },
  ];
};

const clearAllHighlights = (chart: Highcharts.Chart) => {
  chart.tooltip?.hide();
  const chartWithHover = chart as Highcharts.Chart & { __competitorHoverMarkers?: Highcharts.SVGElement[] };
  chartWithHover.__competitorHoverMarkers?.forEach(el => el.destroy());
  chartWithHover.__competitorHoverMarkers = [];
  chart.series.forEach(series => {
    series.points.forEach(point => clearPointHoverBackdrop(point));
  });
};

const clearCompetitorHoverMarkers = (chart: Highcharts.Chart) => {
  const chartWithHover = chart as Highcharts.Chart & { __competitorHoverMarkers?: Highcharts.SVGElement[] };
  chartWithHover.__competitorHoverMarkers?.forEach(el => el.destroy());
  chartWithHover.__competitorHoverMarkers = [];
};

const applyCompetitorHoverMarkers = (chart: Highcharts.Chart, x: number) => {
  clearCompetitorHoverMarkers(chart);
  const chartWithHover = chart as Highcharts.Chart & { __competitorHoverMarkers?: Highcharts.SVGElement[] };

  const markers: Highcharts.SVGElement[] = [];

  chart.series.forEach(series => {
    const seriesWithCustom = series as Highcharts.Series & { options?: Highcharts.SeriesOptionsType & { custom?: { isCompetitor?: boolean } } };
    const isCompetitorSeries = !!seriesWithCustom.options?.custom?.isCompetitor;
    if (!isCompetitorSeries || !series.visible) return;

    const closestPoint = series.points.reduce<{ point: Highcharts.Point | null; dx: number }>(
      (best, pt) => {
        const dx = Math.abs((pt.x ?? 0) - x);
        if (!best.point || dx < best.dx) return { point: pt, dx };
        return best;
      },
      { point: null, dx: Number.POSITIVE_INFINITY },
    ).point;

    if (!closestPoint) return;

    const pt = closestPoint as Highcharts.Point & { plotX?: number; plotY?: number };
    if (pt.plotX === undefined || pt.plotY === undefined) return;

    const cx = chart.plotLeft + pt.plotX;
    const cy = chart.plotTop + pt.plotY;
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return;

    const seriesColor = (series as unknown as { color?: string }).color;
    const fill = (seriesColor || (series.options as Highcharts.SeriesOptionsType).color || FALLBACK_MARKER_COLOR) as string;
    const { backdrop, marker } = applyHoverBackdropAt(chart, cx, cy, fill);
    markers.push(backdrop, marker);
  });

  chartWithHover.__competitorHoverMarkers = markers;
};

const isMouseOverBullet = (chart: Highcharts.Chart, mouseX: number, mouseY: number): boolean => {
  const hitRadius = HOVER_INTERACTION_RADIUS_PX;

  return chart.series.some(series => {
    if (!series.visible || series.options.type !== 'scatter') return false;
    return series.points.some(point => {
      const ptWithGraphic = point as Highcharts.Point & { plotX?: number; plotY?: number; graphic?: Highcharts.SVGElement };
      if (!ptWithGraphic.graphic || ptWithGraphic.plotX === undefined || ptWithGraphic.plotY === undefined) return false;
      const px = chart.plotLeft + ptWithGraphic.plotX;
      const py = chart.plotTop + ptWithGraphic.plotY;
      const dx = mouseX - px;
      const dy = mouseY - py;
      return dx * dx + dy * dy <= hitRadius * hitRadius;
    });
  });
};

const buildChartEvents = (indicatorBandConfigs: IndicatorBandConfig[]): Highcharts.ChartEventsOptions => ({
  load: function () {
    syncIndicatorBands(this, indicatorBandConfigs);

    const chartEl = this.container;

    chartEl.addEventListener('mousemove', (e: MouseEvent) => {
      const rect = chartEl.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      if (!isMouseOverBullet(this, mouseX, mouseY)) {
        clearAllHighlights(this);
      }
    });

    chartEl.addEventListener('mouseleave', () => {
      clearAllHighlights(this);
    });
  },
  render: function () {
    syncIndicatorBands(this, indicatorBandConfigs);
    organizeLegendWithBottomRow(this); // Organiza a legenda na parte inferior do gráfico
  },
});

const buildChartBase = (indicatorBandConfigs: IndicatorBandConfig[]): Highcharts.ChartOptions => ({
  type: 'spline',
  backgroundColor: 'transparent',
  height: CHART_HEIGHT_PX,
  spacingTop: CHART_SPACING_TOP_PX,
  events: buildChartEvents(indicatorBandConfigs),
  style: {
    fontFamily: FONT_FAMILY,
  },
});

// Constrói as opções do eixo X do gráfico, valores em porcentagem eixo HORIZONTAL
const buildXAxisOptions = (chartData: ChartConfig, indicatorsContext: ChartIndicatorsContext): Highcharts.XAxisOptions => ({
  type: 'linear',
  title: {
    text: chartData.xAxis.title,
    margin: 12,
    style: {
      fontSize: '12px',
      fontWeight: '600',
      color: COLORS.gray800,
    },
  },
  min: chartData.xAxis.min,
  max: chartData.xAxis.max,
  breaks: getXAxisBreaks(chartData),
  showFirstLabel: true,
  showLastLabel: true,
  labels: {
    allowOverlap: false,
    reserveSpace: true,
    formatter: function () {
      return `${Math.round(Number(this.value) * 100)}%`;
    },
    style: {
      fontSize: '10px',
      color: COLORS.gray700,
    },
  },
  lineColor: COLORS.gray300,
  tickColor: COLORS.gray300,
  minPadding: X_AXIS_MIN_MAX_PADDING_RATIO,
  maxPadding: X_AXIS_MIN_MAX_PADDING_RATIO,
  startOnTick: false,
  endOnTick: false,
});

// Constrói as opções do eixo Y do gráfico, valores em porcentagem eixo VERTICAL
const buildYAxisOptions = (chartData: ChartConfig, _competitorDisplayMode?: ShareType | null): Highcharts.YAxisOptions => {
  return {
    title: {
      text: chartData.yAxis.title,

      style: {
        fontSize: '10px',
        fontWeight: '600',
        color: COLORS.gray800,
        textTransform: 'uppercase',
      },
    },
    labels: {
      reserveSpace: true,
      enabled: true,
      format: '{value}%',
      style: {
        fontSize: '10px',
        color: COLORS.gray700,
      },
    },
    endOnTick: false,
    gridLineColor: COLORS.gray300,
    gridLineWidth: 1,
  };
};

const buildTooltipOptions = (): Highcharts.TooltipOptions => ({
  shared: false,
  useHTML: true,
  snap: 5,
  hideDelay: 0,
  backgroundColor: 'transparent',
  borderColor: 'transparent',
  borderRadius: 0,
  borderWidth: 0,
  shadow: false,
  padding: 0,
  style: {
    fontSize: '12px',
    fontFamily: FONT_FAMILY,
  },
  positioner: function (labelWidth, labelHeight, point) {
    const plotLeft = this.chart.plotLeft;
    const plotTop = this.chart.plotTop;
    const plotWidth = this.chart.plotWidth;
    const chartWidth = this.chart.chartWidth;
    const chartHeight = this.chart.chartHeight;
    /** Evita tooltip em cima do ponto / do cursor: prioriza à direita e um pouco acima. */
    const OFFSET_X = 22;
    const OFFSET_Y_ABOVE = 20;

    let x = plotLeft + point.plotX + OFFSET_X;
    if (x + labelWidth > plotLeft + plotWidth) {
      x = plotLeft + point.plotX - labelWidth - OFFSET_X;
    }

    const maxX = Math.max(0, chartWidth - labelWidth);
    x = Math.max(0, Math.min(x, maxX));

    let y = plotTop + point.plotY - labelHeight - OFFSET_Y_ABOVE;
    if (y < plotTop + 4) {
      y = plotTop + point.plotY + OFFSET_Y_ABOVE;
    }
    const maxY = Math.max(0, chartHeight - labelHeight - 100);
    y = Math.max(0, Math.min(y, maxY));

    return { x, y };
  },
});

const LEGEND_COMPETITOR_NAME_MAX_LENGTH = 16;

const truncateCompetitorLegendName = (name: string): string =>
  name.length > LEGEND_COMPETITOR_NAME_MAX_LENGTH ? `${name.slice(0, LEGEND_COMPETITOR_NAME_MAX_LENGTH)}...` : name;

const buildLegendLabel = (
  name: string,
  volumeSeriesName?: string,
  valueSeriesName?: string,
  optimalIndicatorName?: string,
  competitorColorMap?: Map<string, string>,
  curvesClient?: string | null,
  hasCompetitors?: boolean,
  shouldTruncateCompetitorLegendNames?: boolean,
) => {
  const inlineStyle = `display:inline-flex;align-items:center;gap:${LEGEND_ITEM_GAP_PX}px;text-transform: uppercase;`;
  const inlineStyleVertical = `display:inline-flex;align-items:center;gap:${LEGEND_ITEM_GAP_PX}px;`;

  const clientLabel = curvesClient?.trim() ? curvesClient.trim() : 'Client';
  const safeClientLabel = clientLabel.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const competitorColor = competitorColorMap?.get(name);

  if (name === volumeSeriesName) {
    return `<span style="${inlineStyle}">${svgBlue} ${hasCompetitors ? `${safeClientLabel} VOLUME` : 'Share por Volume'}</span>`;
  }
  if (name === valueSeriesName) {
    return `<span style="${inlineStyle}">${svgValue} ${hasCompetitors ? `${safeClientLabel} VALOR` : 'Share por Valor'}</span>`;
  }
  // Texto "Competitividade ótima" oculto temporariamente.
  // if (name === optimalIndicatorName) return `<span style="${inlineStyleVertical}">${svgOptimal} Competitividade ótima</span>`;
  if (name === optimalIndicatorName) return '';
  if (competitorColor) {
    const displayName = hasCompetitors && shouldTruncateCompetitorLegendNames ? truncateCompetitorLegendName(name) : name;
    return `<span style="${inlineStyleVertical}">${buildCompetitorLegendIcon(competitorColor)} ${displayName}</span>`;
  }

  return `<span style="${inlineStyleVertical}">${svgCompetitividade} Competitividade Infopanel atual</span>`;
};

const buildLegendOptions = (
  seriesContext: ChartSeriesContext,
  indicatorsContext: ChartIndicatorsContext,
  curvesClient?: string | null,
  shouldTruncateCompetitorLegendNames?: boolean,
): Highcharts.LegendOptions => {
  const competitorColorMap = new Map(seriesContext.competitorSeries.map(c => [c.competitorName, c.series.color]));
  const hasCompetitors = seriesContext.competitorSeries.length > 0;

  return {
    enabled: true,
    align: 'center',
    verticalAlign: 'bottom',
    layout: 'horizontal',
    alignColumns: true,
    useHTML: true,
    itemStyle: {
      fontSize: '12px',
      fontWeight: '400',
      color: COLORS.gray700,
    },
    symbolWidth: 0,
    symbolHeight: 0,
    symbolPadding: 0,
    squareSymbol: false,
    itemHoverStyle: {
      color: COLORS.gray700,
    },
    labelFormatter: function () {
      return buildLegendLabel(
        this.name,
        seriesContext.volumeSeries?.name,
        seriesContext.valueSeries?.name,
        indicatorsContext.optimalIndicator?.name,
        competitorColorMap,
        curvesClient,
        hasCompetitors,
        shouldTruncateCompetitorLegendNames,
      );
    },
  };
};

// Constrói os bullets do gráfico
const buildPlotOptions = (indicatorsContext: ChartIndicatorsContext): Highcharts.PlotOptions => ({
  series: {
    linecap: 'round',
    states: {
      hover: {
        enabled: false,
      },
      inactive: { enabled: false },
    },
    events: {
      // Evento de clique na legenda, faz com que os pontos sejam ocultados ou mostrados
      legendItemClick: function () {
        const seriesName = this.name;
        const chart = this.chart;
        const willBeVisible = !this.visible;
        const markerSeries = chart.series.find(s => s.name === `${seriesName}-markers`);
        if (markerSeries) {
          markerSeries.setVisible(willBeVisible, false);
        }
        const pairedValueSeries = chart.series.find(s => s.name === `${seriesName}-value`);
        if (pairedValueSeries) {
          pairedValueSeries.setVisible(willBeVisible, false);
        }
        const optimalName = indicatorsContext.optimalIndicator?.name;
        const cpiName = indicatorsContext.cpiIndicator?.name;
        if (seriesName === optimalName) {
          setIndicatorHidden(chart, INDICATOR_OPTIMAL_BAND_ID, INDICATOR_OPTIMAL_LINE_ID, !willBeVisible);
        } else if (seriesName === cpiName) {
          setIndicatorHidden(chart, INDICATOR_CPI_BAND_ID, INDICATOR_CPI_LINE_ID, !willBeVisible);
        }
      },
    },
  },
  spline: {
    lineWidth: SERIES_LINE_WIDTH_PX,
    marker: {
      enabled: false,
      radius: SPLINE_MARKER_RADIUS_PX,
      lineWidth: 0,
    },
  },
});

const buildLineSeries = (name: string, color: string, data: ChartPoint[], legendIndex: number, zIndex: number): Highcharts.SeriesSplineOptions => ({
  type: 'spline',
  name,
  color,
  lineWidth: SERIES_LINE_WIDTH_PX,
  data: data.map(point => [point.x, point.y]),
  marker: { enabled: false },
  legendIndex,
  enableMouseTracking: false,
  zIndex,
});

const buildBulletSeries = (
  name: string,
  color: string,
  data: ChartPoint[],
  insights: MarketShareInsights,
  curvesClient?: string | null,
  visibleCompetitorSeries?: CompetitorChartSeries[] | null,
  competitorDisplayMode?: import('../../types').ShareType | null,
): Highcharts.SeriesScatterOptions => ({
  type: 'scatter',
  name,
  color,
  data: data.map(point => {
    const highlightPoint = insights.points.find(hp => isSameXAxisValue(hp.x, point.x));
    return {
      x: point.x,
      y: point.y,
      ...(highlightPoint ? { custom: highlightPoint } : {}),
    };
  }),
  marker: { ...buildCircleMarker(color), enabled: true },
  dataLabels: {
    enabled: false,
  },
  showInLegend: false,
  enableMouseTracking: true,
  zIndex: BULLET_Z_INDEX,
  stickyTracking: false,
  tooltip: {
    headerFormat: '',
    pointFormatter: function () {
      const custom = (this.options as Highcharts.PointOptionsObject & { custom?: ChartHighlightPoint }).custom;
      if (!custom) return '';
      if (!competitorDisplayMode) return buildTooltipContent(custom);
      return buildTooltipContentCompetitor(custom, curvesClient, visibleCompetitorSeries, competitorDisplayMode);
    },
  },
  point: {
    events: {
      mouseOver: function () {
        const custom = (this.options as Highcharts.PointOptionsObject & { custom?: ChartHighlightPoint }).custom;
        if (!custom) return;
        const highlightedCoordinates = new Set<string>();

        applyCompetitorHoverMarkers(this.series.chart, custom.x);

        this.series.chart.series.forEach(series => {
          if (series.name === HOVER_TARGET_SERIES_NAME) return;
          series.points.forEach(pt => {
            if (Math.abs(pt.x - custom.x) < AXIS_VALUE_EPSILON) {
              const ptWithGraphic = pt as Highcharts.Point & { graphic?: Highcharts.SVGElement };
              if (!ptWithGraphic.graphic) return;
              const key = `${pt.x.toFixed(4)}:${(pt.y ?? 0).toFixed(4)}`;
              if (highlightedCoordinates.has(key)) return;
              highlightedCoordinates.add(key);
              const fill = ((pt.options as Highcharts.PointOptionsObject).marker?.fillColor ||
                pt.color ||
                series.options.color ||
                FALLBACK_MARKER_COLOR) as string;
              applyPointHoverBackdrop(pt, fill);
            }
          });
        });
      },
      mouseOut: function () {
        this.series.chart.tooltip?.hide();
        clearCompetitorHoverMarkers(this.series.chart);
        this.series.chart.series.forEach(series => {
          series.points.forEach(pt => clearPointHoverBackdrop(pt));
        });
      },
    },
  },
});

// Constrói as séries verticais de indicadores do gráfico
const buildIndicatorScatterSeries = ({
  indicator,
  fallbackName,
  color,
  point,
  legendIndex,
  highlightPoint,
  curvesClient,
  visibleCompetitorSeries,
  competitorDisplayMode,
}: IndicatorScatterSeriesConfig & {
  highlightPoint?: ChartHighlightPoint | null;
  visibleCompetitorSeries?: CompetitorChartSeries[] | null;
  competitorDisplayMode?: import('../../types').ShareType | null;
}): Highcharts.SeriesScatterOptions => ({
  type: 'scatter',
  name: indicator?.name || fallbackName,
  color,
  data: point && point.y !== null ? [{ x: point.x, y: point.y, ...(highlightPoint ? { custom: highlightPoint } : {}) }] : [],
  marker: buildCircleMarker(color),
  ...(typeof legendIndex === 'number' ? { legendIndex } : {}),
  showInLegend: !!indicator && typeof legendIndex === 'number',
  custom: { legendGroup: 'bottom' },
  zIndex: HIGHLIGHT_Z_INDEX,
  enableMouseTracking: !!highlightPoint,
  stickyTracking: false,
  states: DISABLED_HOVER_STATE,
  ...(highlightPoint
    ? {
        tooltip: {
          headerFormat: '',
          pointFormatter: function () {
            const custom = (this.options as Highcharts.PointOptionsObject & { custom?: ChartHighlightPoint }).custom;
            if (!custom) return '';
            if (!competitorDisplayMode) return buildTooltipContent(custom);
            return buildTooltipContentCompetitor(custom, curvesClient, visibleCompetitorSeries, competitorDisplayMode);
          },
        },
        point: {
          events: {
            mouseOver: function () {
              const custom = (this.options as Highcharts.PointOptionsObject & { custom?: ChartHighlightPoint }).custom;
              if (!custom) return;
              applyCompetitorHoverMarkers(this.series.chart, custom.x);
              this.series.chart.series.forEach(series => {
                series.points.forEach(pt => {
                  if (Math.abs(pt.x - custom.x) < AXIS_VALUE_EPSILON) {
                    const ptWithGraphic = pt as Highcharts.Point & { graphic?: Highcharts.SVGElement };
                    if (!ptWithGraphic.graphic) return;
                    const fill = ((pt.options as Highcharts.PointOptionsObject).marker?.fillColor ||
                      pt.color ||
                      series.options.color ||
                      FALLBACK_MARKER_COLOR) as string;
                    applyPointHoverBackdrop(pt, fill);
                  }
                });
              });
            },
            mouseOut: function () {
              clearCompetitorHoverMarkers(this.series.chart);
              this.series.chart.series.forEach(series => {
                series.points.forEach(pt => clearPointHoverBackdrop(pt));
              });
            },
          },
        },
      }
    : {}),
});

// Constrói as linhas do gráfico
const buildSeriesOptions = ({
  insights,
  seriesContext,
  indicatorsContext,
  currentUsesSeriesColor,
  curvesClient,
  competitorDisplayMode,
}: ChartOptionsContext): Highcharts.SeriesOptionsType[] => {
  const currentVolumeColor = currentUsesSeriesColor ? COLORS.blue400 : COLORS.gray700;
  const currentValueColor = currentUsesSeriesColor ? COLORS.pink400 : COLORS.gray700;
  // Bullet verde da competitividade ótima oculto: mesma cor das séries (azul / pink).
  // const optimalVolumeColor = optimalUsesSeriesColor ? COLORS.blue400 : COLORS.green400;
  // const optimalValueColor = optimalUsesSeriesColor ? COLORS.pink400 : COLORS.green400;
  const optimalVolumeColor = COLORS.blue400;
  const optimalValueColor = COLORS.pink400;

  const isMedleyCompetitor = (competitorName: string): boolean => /\bmedley\b/i.test(competitorName.trim());
  const competitorLegendStartIndex = 4;
  const medleyCompetitorCount = seriesContext.competitorSeries.filter(c => isMedleyCompetitor(c.competitorName)).length;

  return [
    buildLineSeries(seriesContext.volumeSeries.name, COLORS.blue400, seriesContext.volumeLineData, 0, SPLINE_VOLUME_Z_INDEX),
    buildLineSeries(seriesContext.valueSeries.name, COLORS.pink400, seriesContext.valueLineData, 1, SPLINE_VALUE_Z_INDEX),
    buildBulletSeries(
      `${seriesContext.volumeSeries.name}-markers`,
      COLORS.blue400,
      seriesContext.shareVolumeData,
      insights,
      curvesClient,
      seriesContext.competitorSeries,
      competitorDisplayMode ?? null,
    ),
    buildBulletSeries(
      `${seriesContext.valueSeries.name}-markers`,
      COLORS.pink400,
      seriesContext.shareValueData,
      insights,
      curvesClient,
      seriesContext.competitorSeries,
      competitorDisplayMode ?? null,
    ),
    buildIndicatorScatterSeries({
      indicator: indicatorsContext.cpiIndicator,
      fallbackName: 'Competitividade Infopanel atual',
      color: currentVolumeColor,
      point: insights.current ? { x: insights.current.x, y: insights.current.volumeShare } : null,
      legendIndex: 10000,
      highlightPoint: insights.current,
      curvesClient,
      visibleCompetitorSeries: seriesContext.competitorSeries,
      competitorDisplayMode: competitorDisplayMode ?? null,
    }),
    buildIndicatorScatterSeries({
      fallbackName: `${indicatorsContext.cpiIndicator?.name || 'Competitividade Infopanel atual'}-value`,
      color: currentValueColor,
      point: insights.current ? { x: insights.current.x, y: insights.current.valueShare } : null,
      highlightPoint: insights.current,
      curvesClient,
      visibleCompetitorSeries: seriesContext.competitorSeries,
      competitorDisplayMode: competitorDisplayMode ?? null,
    }),
    buildIndicatorScatterSeries({
      indicator: indicatorsContext.optimalIndicator,
      fallbackName: 'Competitividade ótima',
      color: optimalVolumeColor,
      point: insights.optimal ? { x: insights.optimal.x, y: insights.optimal.volumeShare } : null,
      // legendIndex: 10001,
      highlightPoint: insights.optimal,
      curvesClient,
      visibleCompetitorSeries: seriesContext.competitorSeries,
      competitorDisplayMode: competitorDisplayMode ?? null,
    }),
    buildIndicatorScatterSeries({
      fallbackName: `${indicatorsContext.optimalIndicator?.name || 'Competitividade ótima'}-value`,
      color: optimalValueColor,
      point: insights.optimal ? { x: insights.optimal.x, y: insights.optimal.valueShare } : null,
      highlightPoint: insights.optimal,
      curvesClient,
      visibleCompetitorSeries: seriesContext.competitorSeries,
      competitorDisplayMode: competitorDisplayMode ?? null,
    }),
    // COMPETIDORES
    ...seriesContext.competitorSeries.map(
      (comp, idx): Highcharts.SeriesSplineOptions => ({
        legendIndex: isMedleyCompetitor(comp.competitorName) ? competitorLegendStartIndex : competitorLegendStartIndex + medleyCompetitorCount + idx,
        type: 'spline',
        name: comp.competitorName,
        color: comp.series.color,
        lineWidth: 1,
        data: comp.series.data.map(point => [point.x, point.y]),
        marker: {
          enabled: false,
        },
        showInLegend: true,
        enableMouseTracking: false,
        stickyTracking: false,
        tooltip: { enabled: false },
        custom: { isCompetitor: true },
        zIndex: SPLINE_VALUE_Z_INDEX,
      }),
    ),
  ];
};

export const buildMarketShareChartOptions = ({
  chartData,
  insights,
  seriesContext,
  indicatorsContext,
  currentUsesSeriesColor,
  optimalUsesSeriesColor,
  curvesClient,
  competitorDisplayMode,
  shouldTruncateCompetitorLegendNames,
}: ChartOptionsContext): Highcharts.Options => ({
  chart: buildChartBase(indicatorsContext.indicatorBandConfigs),
  title: { text: undefined },
  credits: { enabled: false },
  exporting: { enabled: false },
  xAxis: buildXAxisOptions(chartData, indicatorsContext),
  yAxis: buildYAxisOptions(chartData, competitorDisplayMode),
  tooltip: buildTooltipOptions(),
  legend: buildLegendOptions(seriesContext, indicatorsContext, curvesClient, shouldTruncateCompetitorLegendNames),
  plotOptions: buildPlotOptions(indicatorsContext),
  series: buildSeriesOptions({
    chartData,
    insights,
    seriesContext,
    indicatorsContext,
    currentUsesSeriesColor,
    optimalUsesSeriesColor,
    curvesClient,
    competitorDisplayMode,
  }),
});
